import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { showToast } from '../toast';
import { colors, radius } from '../theme';
import { useAuth } from '../context/AuthContext';

const API = 'https://api.eighteen-media.id';

export default function Profile({ onBack, showInstall, onInstall }) {
  const { client } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [logoStatus, setLogoStatus] = useState(null);
  const [ytEmail, setYtEmail] = useState('');
  const [ytEmails, setYtEmails] = useState([]);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showPassForm, setShowPassForm] = useState(false);
  const [passForm, setPassForm] = useState({ current: '', new: '', confirm: '' });
  const [igStatus, setIgStatus] = useState(null);
  const [igLoading, setIgLoading] = useState(false);
  const [igApiKeyInput, setIgApiKeyInput] = useState('');
  const [showIgManual, setShowIgManual] = useState(false);
  const fileInputRef = useRef();

  const showMsg = (text, type = 'success') => {
    showToast(text, type); // juga melayang di bawah layar — kotak di atas hilang 4 dtk & sering di luar layar
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sub, logo, emails, ig] = await Promise.all([
        axios.get(`${API}/api/payment/subscription`),
        axios.get(`${API}/api/logo/status`),
        axios.get(`${API}/api/notify/yt-emails`),
        axios.get(`${API}/api/instagram/status`).catch(()=>({data:{connected:false,hasPostizKey:false}})),
      ]);
      setSubscription(sub.data);
      setLogoStatus(logo.data);
      setYtEmails(emails.data);
      setIgStatus(ig.data);
    } catch (e) {}
  };

  const setManualApiKey = async () => {
    if (!igApiKeyInput.trim()) return;
    setIgLoading(true);
    try {
      await axios.post(`${API}/api/instagram/set-api-key`, { api_key: igApiKeyInput.trim() });
      showMsg('API key Postiz berhasil disimpan!');
      setIgApiKeyInput('');
      setShowIgManual(false);
      fetchData();
    } catch (err) {
      showMsg(err.response?.data?.error || 'API key tidak valid', 'error');
    } finally { setIgLoading(false); }
  };

  const disconnectInstagram = async () => {
    if (!window.confirm('Disconnect semua akun Instagram?')) return;
    try {
      await axios.delete(`${API}/api/instagram/disconnect`);
      showMsg('Instagram berhasil di-disconnect');
      fetchData();
    } catch (err) {
      showMsg('Gagal disconnect', 'error');
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) { showMsg('Hanya PNG dan JPG', 'error'); return; }
    if (file.size > 2 * 1024 * 1024) { showMsg('Maksimal 2MB', 'error'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      await axios.post(`${API}/api/logo/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMsg('✅ Logo watermark berhasil diupload!');
      fetchData();
    } catch (err) {
      showMsg(err.response?.data?.error || 'Gagal upload logo', 'error');
    } finally { setLoading(false); }
  };

  const deleteLogo = async () => {
    if (!window.confirm('Hapus logo watermark?')) return;
    try {
      await axios.delete(`${API}/api/logo`);
      showMsg('Logo dihapus');
      fetchData();
    } catch (e) { showMsg('Gagal hapus logo', 'error'); }
  };

  const addYtEmail = async () => {
    if (!ytEmail.trim() || !ytEmail.includes('@')) { showMsg('Email tidak valid', 'error'); return; }
    try {
      await axios.post(`${API}/api/notify/yt-email`, { yt_email: ytEmail.trim() });
      showMsg('✅ Email didaftarkan! Admin akan segera setup akun.');
      setYtEmail('');
      fetchData();
    } catch (err) {
      showMsg(err.response?.data?.error || 'Gagal daftar email', 'error');
    }
  };

  const removeYtEmail = async (id) => {
    try {
      await axios.delete(`${API}/api/notify/yt-email/${id}`);
      showMsg('Email dihapus');
      fetchData();
    } catch (e) { showMsg('Gagal hapus', 'error'); }
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    if (passForm.new !== passForm.confirm) { showMsg('Password baru tidak cocok', 'error'); return; }
    if (passForm.new.length < 6) { showMsg('Password minimal 6 karakter', 'error'); return; }
    try {
      await axios.post(`${API}/api/auth/update-password`, {
        current_password: passForm.current,
        new_password: passForm.new
      });
      showMsg('✅ Password berhasil diupdate!');
      setPassForm({ current: '', new: '', confirm: '' });
      setShowPassForm(false);
    } catch (err) {
      showMsg(err.response?.data?.error || 'Gagal update password', 'error');
    }
  };

  const PLAN_INFO = {
    starter: { color: colors.textSecondary, channels: 4, daily: 4 },
    medium:  { color: colors.accent, channels: 8, daily: 6 },
    mega:    { color: '#10b981', channels: 20, daily: 8 },
  };

  const planInfo = subscription ? PLAN_INFO[subscription.plan] : null;
  const daysLeft = subscription ? Math.max(0, Math.ceil((new Date(subscription.expires_at) - new Date()) / (1000*60*60*24))) : 0;

  return (
    <div style={S.container}>
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn}>← Kembali</button>
        <div>
          <h1 style={S.title}>Profil Saya</h1>
          <p style={S.subtitle}>Kelola data akun dan pengaturan</p>
        </div>
      </div>
      {showInstall && (
        <div onClick={onInstall} style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,padding:'12px 16px',background:colors.accentMuted,border:`1px solid ${colors.accent}`,borderRadius:10,cursor:'pointer'}}>
          <i className="ti ti-device-mobile-plus" style={{fontSize:22,color:colors.accent}}></i>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:700,color:colors.accent}}>Install Eighteen Media App</div>
            <div style={{fontSize:11,color:colors.textSecondary,marginTop:2}}>Tambahkan ke layar utama untuk akses lebih cepat</div>
          </div>
          <i className="ti ti-chevron-right" style={{fontSize:18,color:colors.accent}}></i>
        </div>
      )}

      {msg.text && (
        <div style={{...S.msgBox, background:msg.type==='error'?'rgba(239,68,68,0.16)':'rgba(16,185,129,0.16)', color:msg.type==='error'?'#ef4444':'#10b981', marginBottom:20}}>
          {msg.type==='error'?'❌':'✅'} {msg.text}
        </div>
      )}

      <div style={S.grid}>
        {/* Data Diri */}
        <div style={S.card}>
          <h2 style={S.cardTitle}>👤 Data Diri</h2>
          <div style={S.avatarRow}>
            <div style={S.bigAvatar}>{client?.name?.charAt(0)?.toUpperCase()}</div>
            <div>
              <div style={S.bigName}>{client?.name}</div>
              <div style={S.bigEmail}>{client?.email}</div>
            </div>
          </div>
          <div style={S.infoGrid}>
            {[
              {label:'Nama Lengkap', value:client?.name},
              {label:'Email', value:client?.email},
              {label:'Nama Artis', value:client?.artist_name||'-'},
              {label:'WhatsApp', value:client?.phone||'-'},
            ].map(item=>(
              <div key={item.label} style={S.infoItem}>
                <div style={S.infoLabel}>{item.label}</div>
                <div style={S.infoValue}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Update Password */}
          <div style={{marginTop:16}}>
            <button onClick={()=>setShowPassForm(!showPassForm)} style={{...S.btnOutline, width:'100%', fontSize:13}}>
              🔐 {showPassForm ? 'Batal' : 'Ganti Password'}
            </button>
            {showPassForm && (
              <form onSubmit={updatePassword} style={{marginTop:12}}>
                {[
                  {key:'current', label:'Password Lama', placeholder:'••••••••'},
                  {key:'new', label:'Password Baru', placeholder:'Min 6 karakter'},
                  {key:'confirm', label:'Konfirmasi Password Baru', placeholder:'Ulangi password baru'},
                ].map(f=>(
                  <div key={f.key} style={{marginBottom:10}}>
                    <label style={S.infoLabel}>{f.label}</label>
                    <input type="password" placeholder={f.placeholder} value={passForm[f.key]}
                      onChange={e=>setPassForm({...passForm,[f.key]:e.target.value})}
                      style={{width:'100%',padding:'9px 12px',background:colors.bgCardMuted,border:`1px solid ${colors.border}`,borderRadius:8,color:colors.textPrimary,fontSize:13,outline:'none'}}
                      required />
                  </div>
                ))}
                <button type="submit" className="btn btn-gold" style={{width:'100%',padding:'10px',fontSize:13}}>
                  Update Password
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Instagram */}
        <div style={S.card}>
          <h2 style={S.cardTitle}>📸 Instagram</h2>
          {igStatus?.connected ? (
            <div>
              {/* List semua akun IG yang terconnect */}
              {(igStatus.integrations || []).map((ig, idx) => (
                <div key={ig.id || idx} style={{padding:'10px 14px',background:'rgba(16,185,129,0.16)',borderRadius:10,marginBottom:8,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:16}}>✅</span>
                    <div>
                      <div style={{fontSize:12,fontWeight:600,color:'#10b981'}}>{ig.name || 'Instagram'}</div>
                      {ig.profile && <div style={{fontSize:11,color:'#6ee7b7'}}>@{ig.profile}</div>}
                    </div>
                  </div>
                </div>
              ))}
              {/* Tombol tambah akun IG baru */}
              <a href="/bridge.html" target="_blank" rel="noreferrer"
                style={{display:'block',textAlign:'center',padding:'8px 14px',background:'transparent',border:`1px solid ${colors.accent}`,color:colors.accent,borderRadius:8,fontSize:12,textDecoration:'none',marginBottom:8}}>
                ➕ Tambah Akun Instagram
              </a>
              <button onClick={disconnectInstagram} style={{...S.btnOutline, width:'100%', fontSize:12, borderColor:'#ef4444', color:'#ef4444'}}>
                🔌 Disconnect Semua
              </button>
            </div>
          ) : igStatus?.hasPostizKey ? (
            <div>
              <div style={{padding:'12px 14px',background:'rgba(255,87,34,0.16)',borderRadius:10,marginBottom:12,fontSize:13,color:colors.accent}}>
                ⚠️ Akun siap, tapi Instagram belum di-connect.
              </div>
              <a href="/bridge.html" target="_blank" rel="noreferrer"
                style={{display:'block',textAlign:'center',padding:'10px 14px',background:colors.accent,color:'#FFFFFF',borderRadius:8,fontSize:13,fontWeight:700,textDecoration:'none'}}>
                🔗 Connect Instagram Sekarang
              </a>
              <p style={{fontSize:11,color:colors.textSecondary,marginTop:8,lineHeight:1.5}}>
                Pastikan akun Instagram kamu sudah tipe Business atau Creator. Login menggunakan akun yang sudah disiapkan admin.
              </p>
            </div>
          ) : (
            <div>
              <div style={{padding:'12px 14px',background:colors.bgCardMuted,borderRadius:10,marginBottom:12,fontSize:13,color:colors.textSecondary}}>
                Akun Instagram belum disetup. Hubungi admin Eighteen Media untuk aktivasi, atau masukkan API key Postiz secara manual jika kamu sudah punya.
              </div>
              {!showIgManual ? (
                <button onClick={()=>setShowIgManual(true)} style={{...S.btnOutline, width:'100%', fontSize:13}}>
                  🔑 Punya API Key Postiz?
                </button>
              ) : (
                <div>
                  <input type="text" placeholder="Masukkan Postiz API key..." value={igApiKeyInput} onChange={e=>setIgApiKeyInput(e.target.value)}
                    style={{width:'100%',padding:'10px 14px',background:colors.bgCardMuted,border:`1px solid ${colors.border}`,borderRadius:8,color:colors.textPrimary,fontSize:12,marginBottom:8,fontFamily:'monospace'}} />
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={setManualApiKey} disabled={igLoading} style={{...S.btnGold, flex:1, fontSize:13}}>
                      {igLoading?'...':'Simpan'}
                    </button>
                    <button onClick={()=>{setShowIgManual(false);setIgApiKeyInput('');}} style={{...S.btnOutline, flex:1, fontSize:13}}>
                      Batal
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Subscription */}
        <div style={{...S.card, borderColor:planInfo?planInfo.color:colors.border}}>
          <h2 style={S.cardTitle}>⭐ Status Langganan</h2>
          {subscription ? (
            <div>
              <div style={S.planBadge}>
                <span style={{...S.planName, color:planInfo?.color}}>{subscription.plan?.toUpperCase()}</span>
                <span className="badge badge-green">Aktif</span>
              </div>
              <div style={S.planStats}>
                {[
                  {label:'Max Channel', value:subscription.max_channels},
                  {label:'Limit/Hari', value:`${planInfo?.daily||'-'}x upload`},
                  {label:'Sisa Hari', value:`${daysLeft} hari`, color:daysLeft<=7?'#ef4444':'#10b981'},
                  {label:'Expired', value:new Date(subscription.expires_at).toLocaleDateString('id-ID')},
                ].map(s=>(
                  <div key={s.label} style={S.planStat}>
                    <div style={S.planStatLabel}>{s.label}</div>
                    <div style={{...S.planStatValue, color:s.color||colors.textPrimary}}>{s.value}</div>
                  </div>
                ))}
              </div>
              {daysLeft<=7 && (
                <div style={S.expireWarning}>⚠️ Subscription akan expired dalam {daysLeft} hari!</div>
              )}
            </div>
          ) : (
            <div style={S.noSub}>
              <div style={{fontSize:40,marginBottom:8}}>📦</div>
              <div style={{fontWeight:600,marginBottom:8}}>Belum berlangganan</div>
              <div style={{fontSize:13,color:colors.textSecondary,marginBottom:16}}>Pilih plan untuk mulai menggunakan layanan</div>
              <button onClick={onBack} className="btn btn-gold" style={{fontSize:13}}>Lihat Plan →</button>
            </div>
          )}
        </div>

        {/* Email YouTube */}
        <div style={S.card}>
          <h2 style={S.cardTitle}>📺 Email Akun YouTube</h2>
          <p style={S.cardDesc}>
            Daftarkan email Gmail akun YouTube kamu. Admin akan menambahkannya ke Google Console
            agar channel bisa terhubung ke dashboard.
          </p>
          <div style={{display:'flex',gap:8,marginBottom:16}}>
            <input type="email" placeholder="email@gmail.com" value={ytEmail}
              onChange={e=>setYtEmail(e.target.value)}
              onKeyPress={e=>e.key==='Enter'&&addYtEmail()}
              style={{flex:1,padding:'10px 14px',background:colors.bgCardMuted,border:`1px solid ${colors.border}`,borderRadius:8,color:colors.textPrimary,fontSize:14,outline:'none'}}
            />
            <button className="btn btn-gold" style={{flexShrink:0,padding:'10px 16px'}} onClick={addYtEmail}>+ Tambah</button>
          </div>

          {ytEmails.length===0 ? (
            <div style={S.emptySmall}>Belum ada email yang didaftarkan</div>
          ) : (
            <div>
              {ytEmails.map(e=>(
                <div key={e.id} style={{...S.emailItem, borderColor:e.status==='connected'?'#10b981':colors.border}}>
                  <div>
                    <div style={{fontSize:14,color:colors.textPrimary,fontWeight:500}}>{e.email}</div>
                    <div style={{fontSize:11,color:colors.textSecondary,marginTop:2}}>
                      Didaftarkan: {new Date(e.added_at).toLocaleDateString('id-ID')}
                    </div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{
                      padding:'2px 10px',borderRadius:999,fontSize:11,fontWeight:600,
                      background:e.status==='connected'?'rgba(16,185,129,0.16)':'rgba(255,87,34,0.16)',
                      color:e.status==='connected'?'#10b981':colors.accent
                    }}>
                      {e.status==='connected'?'✅ Connected':'⏳ Menunggu admin'}
                    </span>
                    <button onClick={()=>removeYtEmail(e.id)}
                      style={{background:'transparent',border:'none',color:'#ef4444',cursor:'pointer',fontSize:16,padding:'0 4px'}}>
                      ×
                    </button>
                  </div>
                </div>
              ))}
              <div style={{fontSize:12,color:colors.textSecondary,marginTop:12,padding:'8px 12px',background:colors.bgCardMuted,borderRadius:8}}>
                💡 Status akan berubah ke "Connected" setelah admin selesai setup di Google Console
              </div>
            </div>
          )}
        </div>

        {/* Logo Watermark */}
        <div style={S.card}>
          <h2 style={S.cardTitle}>🎨 Logo Watermark</h2>
          <p style={S.cardDesc}>
            Upload logo untuk dijadikan watermark di setiap video. PNG transparan menghasilkan tampilan terbaik.
          </p>

          {logoStatus?.has_logo && (
            <div style={S.logoPreview}>
              <img src={`${API}${logoStatus.url}`} alt="Logo" style={{maxHeight:70,maxWidth:180,objectFit:'contain'}}/>
              <div style={{flex:1}}>
                <div style={{color:'#10b981',fontSize:13,fontWeight:600}}>✅ Logo aktif</div>
                <div style={{color:colors.textSecondary,fontSize:12,marginTop:4}}>Muncul di pojok kanan atas setiap video</div>
              </div>
              <button onClick={deleteLogo} style={{background:'transparent',border:'1px solid #ef4444',color:'#ef4444',padding:'6px 12px',borderRadius:8,cursor:'pointer',fontSize:12,flexShrink:0}}>
                Hapus
              </button>
            </div>
          )}

          <div
            style={{border:`2px dashed ${dragOver?colors.accent:colors.border}`,borderRadius:10,padding:'24px 16px',textAlign:'center',background:dragOver?colors.bgCardMuted:'transparent',cursor:'pointer',transition:'all 0.2s',marginTop:logoStatus?.has_logo?12:0}}
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={e=>{e.preventDefault();setDragOver(false);uploadLogo(e.dataTransfer.files[0]);}}
            onClick={()=>fileInputRef.current?.click()}
          >
            <div style={{fontSize:32,marginBottom:8}}>{loading?'⏳':'🖼️'}</div>
            <div style={{color:colors.textPrimary,fontSize:14,fontWeight:500}}>
              {loading?'Mengupload...' : logoStatus?.has_logo?'Ganti Logo':'Upload Logo Watermark'}
            </div>
            <div style={{color:colors.textSecondary,fontSize:12,marginTop:4}}>Drop file atau klik • PNG / JPG • Maks 2MB</div>
            <div style={{color:colors.accent,fontSize:11,marginTop:6}}>💡 PNG transparan = watermark tanpa background kotak</div>
            <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg" style={{display:'none'}}
              onChange={e=>uploadLogo(e.target.files[0])} />
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  container:{padding:'0 0 40px'},
  header:{display:'flex',alignItems:'flex-start',gap:16,marginBottom:24},
  backBtn:{background:'transparent',border:`1px solid ${colors.border}`,color:colors.textSecondary,padding:'8px 14px',borderRadius:8,cursor:'pointer',fontSize:13,flexShrink:0,marginTop:4},
  title:{fontSize:22,fontWeight:700,color:colors.textPrimary},
  subtitle:{fontSize:13,color:colors.textSecondary,marginTop:2},
  msgBox:{padding:'10px 14px',borderRadius:8,fontSize:14},
  grid:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))',gap:20},
  card:{background:colors.bgCard,border:`2px solid ${colors.border}`,borderRadius:12,padding:24},
  cardTitle:{fontSize:15,fontWeight:600,color:colors.textPrimary,marginBottom:16},
  cardDesc:{fontSize:13,color:colors.textSecondary,marginBottom:16,lineHeight:1.6},
  avatarRow:{display:'flex',gap:14,alignItems:'center',marginBottom:20,padding:16,background:colors.bgCardMuted,borderRadius:10},
  bigAvatar:{width:52,height:52,borderRadius:'50%',background:colors.accent,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:22,color:'#FFFFFF',flexShrink:0},
  bigName:{fontWeight:700,fontSize:16,color:colors.textPrimary},
  bigEmail:{fontSize:13,color:colors.textSecondary,marginTop:2},
  infoGrid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12},
  infoItem:{background:colors.bgCardMuted,borderRadius:8,padding:'10px 12px'},
  infoLabel:{fontSize:11,color:colors.textSecondary,marginBottom:4},
  infoValue:{fontSize:14,fontWeight:500,color:colors.textPrimary},
  btnOutline:{background:'transparent',border:`1px solid ${colors.border}`,color:colors.textSecondary,padding:'8px 14px',borderRadius:8,cursor:'pointer',fontSize:13},
  planBadge:{display:'flex',alignItems:'center',gap:12,marginBottom:20},
  planName:{fontSize:24,fontWeight:700},
  planStats:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12},
  planStat:{background:colors.bgCardMuted,borderRadius:8,padding:'10px 12px'},
  planStatLabel:{fontSize:11,color:colors.textSecondary,marginBottom:4},
  planStatValue:{fontSize:16,fontWeight:600},
  expireWarning:{background:'rgba(239,68,68,0.16)',border:'1px solid #ef4444',color:'#ef4444',padding:'10px 14px',borderRadius:8,fontSize:13},
  noSub:{textAlign:'center',padding:'20px 0',color:colors.textSecondary},
  emptySmall:{textAlign:'center',padding:'20px',color:colors.textSecondary,fontSize:13,background:colors.bgCardMuted,borderRadius:8},
  emailItem:{display:'flex',flexWrap:'wrap',justifyContent:'space-between',alignItems:'center',gap:8,padding:'10px 14px',background:colors.bgCardMuted,borderRadius:8,marginBottom:8,border:`1px solid ${colors.border}`},
  logoPreview:{display:'flex',alignItems:'center',gap:14,padding:16,background:colors.bgCardMuted,borderRadius:10,marginBottom:12},
};
